import { computed, ref } from '@vue/composition-api'
import {
  convertCoordinatesFile,
  convertCoordinatesJson,
  extractConvertCoordinatesError,
} from '@/api/coordinate-conversion/convertCoordinates'

function emptySummary() {
  return { total: 0, converted: 0, failed: 0 }
}

export default function useConvertCoordinates() {
  const loading = ref(false)
  const response = ref(null)
  const error = ref(null)

  const resultData = computed(() => (response.value && response.value.data) || null)
  const coordinates = computed(() => (resultData.value && resultData.value.coordinates) || [])
  const failed = computed(() => (resultData.value && resultData.value.failed) || [])
  const summary = computed(() => (resultData.value && resultData.value.summary) || emptySummary())
  const fileType = computed(() => (resultData.value && resultData.value.file_type) || null)
  const source = computed(() => (resultData.value && resultData.value.source) || null)
  const message = computed(() => (response.value && response.value.message) || '')

  const hasResult = computed(() => Boolean(resultData.value))
  const isPartialSuccess = computed(() => (
    hasResult.value && Number(summary.value.failed) > 0 && Number(summary.value.converted) > 0
  ))
  const isFailedOnly = computed(() => (
    hasResult.value && Number(summary.value.converted) === 0 && Number(summary.value.failed) > 0
  ))
  const isFullSuccess = computed(() => (
    hasResult.value && Number(summary.value.converted) > 0 && Number(summary.value.failed) === 0
  ))

  const reset = () => {
    response.value = null
    error.value = null
  }

  const convertJson = async payload => {
    loading.value = true
    error.value = null
    response.value = null
    try {
      const result = await convertCoordinatesJson(payload)
      response.value = result
      return result
    } catch (err) {
      error.value = extractConvertCoordinatesError(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const convertFile = async payload => {
    loading.value = true
    error.value = null
    response.value = null
    try {
      const result = await convertCoordinatesFile(payload)
      response.value = result
      return result
    } catch (err) {
      error.value = extractConvertCoordinatesError(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    response,
    error,
    resultData,
    coordinates,
    failed,
    summary,
    fileType,
    source,
    message,
    hasResult,
    isPartialSuccess,
    isFailedOnly,
    isFullSuccess,
    convertJson,
    convertFile,
    reset,
  }
}
